<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router' // 1. Importujemy useRouter
import AppLoader from '@/components/AppLoader.vue'
import apiClient from '@/api'

const router = useRouter() // 2. Tworzymy instancję routera

// Stany formularza
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
import { webAlert } from '@/utils/dialog'

// Walidacja i obsługa wysyłania
const handleLogin = async () => {
    errorMessage.value = ''

    if (!email.value || !password.value) {
        errorMessage.value = 'Uzupełnij adres e-mail oraz hasło.'
        return
    }

    isLoading.value = true
 
    try {
        const response = await apiClient.post({
                email: email.value,
                password: password.value,
                action: 'login'
        })

        const data = response.data;

        console.log(data)

        if (data.status == 'success') {
            // 5. Zapisanie tokenu i przekierowanie
            localStorage.setItem('integrator_token', data.token)
            localStorage.setItem('user_email', data.user.email)
            localStorage.setItem('user_role', data.user.role)

            // Używamy Vue Routera do płynnego przejścia zamiast przeładowywania strony przez window.location
            router.push('/admin/dashboard')
        } else {
            errorMessage.value = data.message;
        }

    } catch (err) {
        // Przechwytuje błędy sieciowe, błędy z `throw new Error` oraz ewentualne awarie parsowania
        webAlert('Nie udało się połączyć z serwerem.')
    } finally {
        isLoading.value = false
    }
}
</script>

<template>
    <AppLoader :show="isLoading" text="Trwa logowanie, proszę czekać..." />
    <div class="background-img">
        <div class="min-vh-100 d-flex align-items-center justify-content-center py-5">
            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-12 col-sm-10 col-md-8 col-lg-5 col-xl-4 background-blured ">

                        <!-- Logo i Nagłówek -->
                        <div class="text-center mb-4">

                            <h4 class="fw-bold text-dark mb-1">Panel Admina</h4>
                            <p class="text-muted small">Zaloguj się, aby zarządzać</p>
                        </div>

                        <!-- Kartka z Formularzem -->
                        <div class="card border-0 shadow-sm rounded-3">
                            <div class="card-body p-4">

                                <!-- Komunikat błędu -->
                                <div v-if="errorMessage" class="alert alert-danger d-flex align-items-center mb-3"
                                    role="alert">
                                    <div class="small">{{ errorMessage }}</div>
                                </div>

                                <form @submit.prevent="handleLogin" novalidate>

                                    <!-- Pole Email -->
                                    <div class="mb-3">
                                        <label for="emailInput" class="form-label fw-semibold small">Adres
                                            e-mail</label>
                                        <div class="input-group">
                                            <span class="input-group-text bg-white text-muted border-end-0">
                                                <i class="bi bi-envelope"></i>
                                            </span>
                                            <input id="emailInput" v-model.trim="email" type="email"
                                                class="form-control border-start-0 ps-0" placeholder=""
                                                required :disabled="isLoading" />
                                        </div>
                                    </div>

                                    <!-- Pole Hasło z opcją pokazywania -->
                                    <div class="mb-4">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label for="passwordInput"
                                                class="form-label fw-semibold small mb-0">Hasło</label>
                                        </div>
                                        <div class="input-group">
                                            <span class="input-group-text bg-white text-muted border-end-0">
                                                <i class="bi bi-key"></i>
                                            </span>
                                            <input id="passwordInput" v-model="password"
                                                :type="showPassword ? 'text' : 'password'"
                                                class="form-control border-start-0 border-end-0 ps-0"
                                                placeholder="" required :disabled="isLoading" />
                                            <button class="btn btn-outline-secondary border-start-0 bg-white text-muted"
                                                type="button" @click="showPassword = !showPassword"
                                                :disabled="isLoading">
                                                <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <!-- Przycisk Zaloguj -->
                                    <button type="submit"
                                        class="btn btn-primary w-100 py-2 fw-semibold d-flex align-items-center justify-content-center"
                                        :disabled="isLoading">
                                        <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"
                                            role="status"></span>
                                        <i v-else class="bi bi-box-arrow-in-right me-2"></i>
                                        <span>{{ isLoading ? 'Logowanie...' : 'Zaloguj się' }}</span>
                                    </button>

                                </form>
                            </div>
                        </div>

                        <!-- Stopka / Informacja prywatna -->
                        <div class="text-center mt-4">
                            <p class="text-muted small mb-0">
                                <i class="bi bi-lock me-1"></i> Połączenie szyfrowane (SSL)
                            </p>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
/* Poprawka dla estetyki focusu */
.input-group:focus-within .input-group-text,
.input-group:focus-within .form-control,
.input-group:focus-within .btn {
    border-color: var(--login-input-focus-border) !important;
}

.background-img {
    background-image: url('@/assets/background-black.jpeg');
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
}

/* Ciemne półprzezroczyste tło formularza z rozmyciem */
.background-blured {
    background-color: var(--login-bg-card);
    backdrop-filter: blur(8px);
    padding-top: 15px;
    padding-bottom: 15px;
    border-radius: 8px;
    border: 1px solid var(--login-card-border);
}

/* Zjednolicenie ramek i tła w input-group */
.input-group .input-group-text,
.input-group .form-control,
.input-group .btn {
    border-color: var(--login-input-border) !important;
    background-color: var(--login-input-bg);
    color: var(--login-input-text);
}

/* Usuwamy cień przy kliknięciu na przycisk oczka */
.input-group .btn:focus {
    box-shadow: none;
}

/* Zmiana koloru ikony po najechaniu myszką */
.input-group .btn:hover i {
    color: var(--login-btn-hover-icon) !important;
}
</style>